import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  KeyRound, Eye, EyeOff, ExternalLink, Copy, Plus, Trash2, Lock, ShieldCheck, ChevronDown,
} from "lucide-react";

export default function VaultCredentialsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState({}); // portal_key -> {password, username, code, portal_url}
  const [editing, setEditing] = useState(null); // null | new | object
  const [deletingKey, setDeletingKey] = useState(null);
  // Each credential card collapses to a single-line header. Click to expand details.
  const [expandedKey, setExpandedKey] = useState(null);
  const toggleExpand = (k) => setExpandedKey((cur) => cur === k ? null : k);
  // Whole card is collapsible (sensitive content — closed by default; state persisted)
  const [cardOpen, setCardOpen] = useState(() => localStorage.getItem("vault_card_open") === "1");
  const toggleCard = () => setCardOpen((v) => {
    const next = !v;
    localStorage.setItem("vault_card_open", next ? "1" : "0");
    return next;
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/vault/credentials");
      setItems(res.data.credentials || []);
    } catch (e) {
      toast.error("Errore caricamento credenziali");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const reveal = async (portal_key) => {
    if (revealed[portal_key]) {
      setRevealed((p) => { const n = { ...p }; delete n[portal_key]; return n; });
      return;
    }
    try {
      const res = await api.get(`/admin/vault/credentials/${portal_key}/reveal`);
      setRevealed((p) => ({ ...p, [portal_key]: res.data }));
    } catch (e) {
      toast.error("Errore lettura credenziale");
    }
  };

  const copyText = async (text, label) => {
    const ok = await copyToClipboard(text || "");
    if (ok) toast.success(`${label} copiato negli appunti`);
    else toast.error("Impossibile copiare");
  };

  const remove = async (portal_key) => {
    if (!confirm("Eliminare definitivamente questa credenziale?")) return;
    setDeletingKey(portal_key);
    try {
      await api.delete(`/admin/vault/credentials/${portal_key}`);
      toast.success("Credenziale eliminata");
      load();
    } catch {
      toast.error("Errore eliminazione");
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <div data-testid="vault-credentials-card" className="bg-white border border-[#E8E2D9] rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={toggleCard}
        data-testid="vault-card-toggle"
        aria-expanded={cardOpen}
        className="w-full bg-gradient-to-r from-[#2A4F7C] to-[#1E3A5F] text-white px-5 py-4 flex items-center gap-3 hover:from-[#345d92] hover:to-[#244671] transition text-left"
      >
        <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-display text-lg leading-tight">Cassaforte Credenziali</h4>
          <p className="text-xs text-white/80 mt-0.5">
            {cardOpen
              ? "Username e password cifrati AES-256 per i portali esterni obbligatori. Accesso sempre tracciato."
              : `${items.length} credenziali salvate · tocca per aprire`}
          </p>
        </div>
        {cardOpen && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              setEditing({ portal_key: "", label: "", username: "", code: "", password: "", portal_url: "", notes: "" });
            }}
            role="button"
            tabIndex={0}
            data-testid="vault-add-btn"
            className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 text-white border-0 px-3 py-1.5 rounded-md text-sm cursor-pointer"
          >
            <Plus className="w-4 h-4" /> Aggiungi
          </span>
        )}
        <ChevronDown className={`w-5 h-5 text-white/80 transition-transform ${cardOpen ? "rotate-0" : "-rotate-90"}`} />
      </button>

      {cardOpen && (
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="text-sm text-[#635F59] text-center py-6">Caricamento...</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-[#635F59] text-center py-8">
            <Lock className="w-8 h-8 mx-auto mb-2 text-[#C9C2B6]" />
            Nessuna credenziale salvata. Clicca <strong>Aggiungi</strong> per inserire la prima.
          </div>
        ) : (
          items.map((it) => {
            const r = revealed[it.portal_key];
            const isOpen = expandedKey === it.portal_key;
            return (
              <div
                key={it.portal_key}
                data-testid={`vault-item-${it.portal_key}`}
                className={`border border-[#E8E2D9] rounded-lg overflow-hidden transition ${isOpen ? "border-[#AC5D45]/40 shadow-sm" : "hover:border-[#AC5D45]/40"}`}
              >
                {/* Accordion header — clic per espandere/comprimere */}
                <button
                  type="button"
                  onClick={() => toggleExpand(it.portal_key)}
                  className="w-full text-left p-3.5 flex items-center justify-between gap-3 hover:bg-[#FDFBF7] transition"
                  data-testid={`vault-toggle-${it.portal_key}`}
                  aria-expanded={isOpen}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <KeyRound className="w-4 h-4 text-[#AC5D45] shrink-0" />
                    <div className="min-w-0">
                      <h5 className="font-semibold text-[#1E3A5F] truncate">{it.label}</h5>
                      {!isOpen && it.username && <p className="text-[11px] text-[#635F59] truncate">{it.username}</p>}
                    </div>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-[#635F59] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>

                {isOpen && (
                <div className="px-3.5 pb-3.5 border-t border-[#E8E2D9]/60">
                <div className="flex items-start justify-end gap-1 pt-3">
                    {it.portal_url && (
                      <a
                        href={it.portal_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Apri portale"
                        data-testid={`vault-link-${it.portal_key}`}
                        className="w-8 h-8 rounded-md hover:bg-[#F5F1EA] flex items-center justify-center text-[#1E3A5F]"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      onClick={() => setEditing(it)}
                      title="Modifica"
                      data-testid={`vault-edit-${it.portal_key}`}
                      className="w-8 h-8 rounded-md hover:bg-[#F5F1EA] flex items-center justify-center text-[#635F59]"
                    >
                      <Lock className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => remove(it.portal_key)}
                      disabled={deletingKey === it.portal_key}
                      title="Elimina"
                      data-testid={`vault-delete-${it.portal_key}`}
                      className="w-8 h-8 rounded-md hover:bg-rose-50 flex items-center justify-center text-rose-600 disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {it.notes && <p className="text-xs text-[#635F59] italic line-clamp-2 mb-2">{it.notes}</p>}

                <div className="grid sm:grid-cols-2 gap-2 text-sm">
                  <FieldDisplay
                    label="Username"
                    value={it.username || "—"}
                    onCopy={it.username ? () => copyText(it.username, "Username") : null}
                  />
                  {it.code && (
                    <FieldDisplay
                      label="Codice struttura"
                      value={it.code}
                      onCopy={() => copyText(it.code, "Codice")}
                    />
                  )}
                  <FieldDisplay
                    label="Password"
                    value={r ? r.password : (it.password_masked || "••••••••")}
                    mono
                    onCopy={r ? () => copyText(r.password, "Password") : null}
                    actionIcon={r ? EyeOff : Eye}
                    actionLabel={r ? "Nascondi" : "Mostra"}
                    onAction={() => reveal(it.portal_key)}
                    actionTestId={`vault-reveal-${it.portal_key}`}
                  />
                </div>
                </div>
                )}
              </div>
            );
          })
        )}
      </div>
      )}

      {editing && (
        <EditDialog
          initial={editing}
          isNew={!editing.portal_key}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function FieldDisplay({ label, value, mono, onCopy, actionIcon: ActionIcon, actionLabel, onAction, actionTestId }) {
  return (
    <div className="bg-[#FAF7F2] rounded-md px-2.5 py-1.5 flex items-center gap-2 border border-transparent hover:border-[#E8E2D9] transition">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[#635F59]">{label}</div>
        <div className={`text-sm text-[#2D2926] truncate ${mono ? "font-mono" : ""}`}>{value}</div>
      </div>
      {onCopy && (
        <button onClick={onCopy} title="Copia" className="w-7 h-7 rounded hover:bg-white flex items-center justify-center text-[#635F59]">
          <Copy className="w-3.5 h-3.5" />
        </button>
      )}
      {ActionIcon && (
        <button
          onClick={onAction}
          title={actionLabel}
          data-testid={actionTestId}
          className="w-7 h-7 rounded hover:bg-white flex items-center justify-center text-[#1E3A5F]"
        >
          <ActionIcon className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function EditDialog({ initial, isNew, onClose, onSaved }) {
  const [form, setForm] = useState({
    portal_key: initial.portal_key || "",
    label: initial.label || "",
    username: initial.username || "",
    code: initial.code || "",
    password: "",
    portal_url: initial.portal_url || "",
    notes: initial.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.portal_key.trim() || !form.label.trim()) {
      toast.error("Chiave e nome obbligatori");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!isNew && !payload.password) delete payload.password; // keep existing password
      await api.post("/admin/vault/credentials", payload);
      toast.success(isNew ? "Credenziale aggiunta" : "Credenziale aggiornata");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="vault-edit-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nuova credenziale" : "Modifica credenziale"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Chiave portale (slug univoco)</Label>
            <Input
              value={form.portal_key}
              onChange={(e) => set("portal_key", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
              disabled={!isNew}
              placeholder="es. tassa-soggiorno-rc"
              data-testid="vault-input-key"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Nome visibile *</Label>
            <Input value={form.label} onChange={(e) => set("label", e.target.value)} data-testid="vault-input-label" className="mt-1" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Username</Label>
              <Input value={form.username} onChange={(e) => set("username", e.target.value)} data-testid="vault-input-user" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Codice struttura (opzionale)</Label>
              <Input value={form.code} onChange={(e) => set("code", e.target.value)} data-testid="vault-input-code" className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Password {!isNew && <span className="text-[#635F59]">(lascia vuoto per non cambiare)</span>}</Label>
            <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="vault-input-pwd" className="mt-1 font-mono" />
          </div>
          <div>
            <Label className="text-xs">URL portale</Label>
            <Input value={form.portal_url} onChange={(e) => set("portal_url", e.target.value)} placeholder="https://..." data-testid="vault-input-url" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Note</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} data-testid="vault-input-notes" className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="vault-cancel-btn">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="vault-save-btn" className="bg-[#1E3A5F] hover:bg-[#152A45]">
            {saving ? "Salvataggio..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
